import { router, usePage } from "@inertiajs/react"
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { toast } from "sonner"
import { FlowCanvas } from "@/components/flow/flow-canvas"
import { FlowNodeTitleProvider } from "@/components/flow/flow-node-title"
import { FlowNodeVersionsGallery } from "@/components/flow/flow-node-versions-gallery"
import { FlowVersionsProvider } from "@/components/flow/flow-versions-context"
import { FlowBulkRegenerateDialog } from "@/components/flow/flow-bulk-regenerate-dialog"
import { FlowContextMenu, type FlowMenuConnectFrom, type FlowMenuState } from "@/components/flow/flow-context-menu"
import { FlowNodeDock, FlowNodeSideColumn, useFlowNodePanel } from "@/components/flow/flow-node-panel"
import { LibraryPickerDialog } from "@/components/library/library-picker-dialog"
import {
  buildMentionItems,
  type CastRow,
  type LocationRow,
  type PropRow,
} from "@/components/prompt-editor/mention-sources"
import { isFlowBoardTypingTarget, useFlowBoardHistory } from "@/hooks/use-flow-board-history"
import { useTranslation } from "@/hooks/use-translation"
import { confirm } from "@/lib/confirm"
import { mergeFlowNodeRows } from "@/lib/flow-board-history"
import { placeableFlowChains } from "@/lib/flow-chain-catalog"
import { clipboardHasFiles } from "@/lib/clipboard-files"
import {
  flowAttachLimit,
  flowAttachMediaForTarget,
  flowAttachMediaFromContentType,
  flowAttachPosition,
  flowAttachPorts,
  type FlowAttachMedia,
  type FlowAttachOutcome,
  type FlowAttachRequest,
} from "@/lib/flow-attach-input"
import {
  createBlankFlowChain,
  createFlowChain,
  createFlowEdge,
  createFlowNode,
  deleteFlowChain,
  duplicateFlowChain,
  flowChainEditorBackHref,
  flowChainHref,
  deleteFlowNode,
  duplicateFlowNodes,
  parseFlowFocusNodeId,
  stripFlowFocusNodeParam,
  updateFlowNode,
  updateFlowViewport,
} from "@/lib/flow-api"
import {
  flowConnectHandleSide,
  flowConnectNodeOrigin,
  flowConnectOriginForNode,
  flowFreshNodeRatio,
  flowNodeCardSize,
} from "@/lib/flow-node-size"
import {
  flowFileCascadeOffset,
  flowFileNodeTitle,
  flowPastePositions,
  flowUploadingNodeRow,
  partitionFlowFiles,
} from "@/lib/flow-place-files"
import {
  REFERENCE_AUDIO_FILE_ACCEPT,
  REFERENCE_IMAGE_VIDEO_FILE_ACCEPT,
  referenceMaxUploadMb,
} from "@/lib/reference-file-policy"
import { useWorkspaceJobsStore } from "@/stores/workspace-jobs-store"
import type { GenerationJobRow } from "@/types/workspace"
import { useAgentRunsStore } from "@/stores/agent-runs-store"
import type { CanvasModel, CanvasProjectStyles } from "@/types/canvas"
import { flowStoredPorts } from "@/types/flow-connectors"
import {
  flowNodeCanBulkRegenerate,
  flowNodePending,
  flowNodeUploading,
  mergeFlowNodes,
  type FlowBoardRow,
  type FlowChainRow,
  type FlowDefaults,
  type FlowNodeKind,
  type FlowNodeRow,
  type FlowOpenLayout,
} from "@/types/flow"
import type { ProjectSummary } from "@/types/workspace"
import type { PostProductionModelOption, ShotFrameSource } from "@/lib/workspace-api"

const FLOW_NODE_CLIPBOARD_MARKER = "frameriver-flow-nodes"

type FlowNodeClipboard = {
  flowId: number
  nonce: string
  pasteCount: number
  nodes: { id: number; x: number; y: number }[]
}

const SHOT_MEDIA_JOB_KINDS = new Set([
  "storyboard",
  "shooting_board",
  "storyboard_correction",
  "video",
  "video_with_reference_prep",
  "video_with_animatic_reference_prep",
  "byteplus_seedance_video_correction",
  "kling_video_correction",
])

function shotMediaJobActive(job: GenerationJobRow): boolean {
  return job.status === "queued" || job.status === "processing"
}

function sameIdSet(left: readonly number[], right: readonly number[]): boolean {
  if (left.length !== right.length) return false
  const ids = new Set(left)
  return right.every((id) => ids.has(id))
}

function shortcutBlockedByDialog(insideDialog: boolean): boolean {
  const dialogs = document.querySelectorAll("[role='dialog']")
  if (insideDialog) return dialogs.length > 1
  return dialogs.length > 0
}

export type FlowBoardSession = {
  canvas: ReactNode
  sideColumn: ReactNode
  dock: ReactNode
  overlays: ReactNode
  title: string | null
  nodes: FlowNodeRow[]
  renameTitle: (nodeId: number, title: string | null) => void
}

export type UseFlowBoardSessionArgs = {
  project: ProjectSummary | null
  board: Omit<FlowBoardRow, "number"> | null
  nodes: FlowNodeRow[]
  models: CanvasModel[]
  upscaleModels: PostProductionModelOption[]
  defaults: FlowDefaults
  projectStyles: CanvasProjectStyles
  characters: CastRow[]
  locations: LocationRow[]
  propsList: PropRow[]
  canEdit: boolean
  reloadNodes: () => void
  chains: FlowChainRow[]
  onChainsChange: (chains: FlowChainRow[]) => void
  onStructureChange?: () => void
  returnFlow: number | null
  allowChains: boolean
  /** Шаблон в редакторе: палитра прячет его и ссылающиеся на него цепочки, удаление из меню уводит назад по via. */
  editingChainId?: number | null
  /** via для следующей цепочки: родители и текущий шаблон, снаружи внутрь. */
  chainVia?: string | null
  /** Служебная доска цепочки: у начальных нод есть переключатель инпута. */
  chainInputs?: boolean
  /** Замечание к графу цепочки. Пустая строка и null — плашки нет. */
  chainNotice?: string | null
  /** When set, selecting this node replaces the flow page's URL focus. Undefined keeps the URL behavior. */
  selectNodeId?: number | null
  /** On canvas mount: fit every node once, or first arrange the board (saved, editors only). Skips the single-node camera. */
  openLayout?: FlowOpenLayout
  /** The shot panel is itself a dialog. Nested dialogs still block shortcuts. */
  insideDialog?: boolean
}

export function useFlowBoardSession({
  project,
  board,
  nodes,
  models,
  upscaleModels,
  defaults,
  projectStyles,
  characters,
  locations,
  propsList,
  canEdit,
  reloadNodes,
  chains,
  onChainsChange,
  onStructureChange,
  returnFlow,
  allowChains,
  editingChainId = null,
  chainVia = null,
  chainInputs = false,
  chainNotice = null,
  selectNodeId,
  openLayout,
  insideDialog = false,
}: UseFlowBoardSessionArgs): FlowBoardSession {
  const { t } = useTranslation("flow")
  const toastNodeError = useCallback(
    (error: unknown) => {
      toast.error(error instanceof Error ? error.message : t("errors.nodeFailed"))
    },
    [t],
  )
  const projectId = project?.id ?? 0
  const projectName = project?.name ?? ""
  const [nodeRows, setNodeRows] = useState(nodes)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [mentionedNodeIds, setMentionedNodeIds] = useState<number[]>([])
  const [focusNodeId, setFocusNodeId] = useState<number | null>(null)
  const { url: pageUrl } = usePage()
  const consumedFocusUrlRef = useRef<string | null>(null)
  const [selectedEdgeId, setSelectedEdgeId] = useState<number | null>(null)
  const [menu, setMenu] = useState<FlowMenuState | null>(null)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkIds, setBulkIds] = useState<number[]>([])
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [libraryBusy, setLibraryBusy] = useState(false)
  const [versionsNodeId, setVersionsNodeId] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const audioInputRef = useRef<HTMLInputElement>(null)
  const connectAfterFileRef = useRef<FlowMenuConnectFrom | null>(null)
  const placeAt = useRef({ x: 0, y: 0 })
  const agentRuns = useAgentRunsStore((state) => state.runs)
  const jobs = useWorkspaceJobsStore((state) => state.jobs)
  const flowNodeRunStatusRef = useRef<Record<number, string>>({})
  const shotJobStatusRef = useRef<Record<number, string>>({})
  const selectNodeIdRef = useRef(selectNodeId)
  const openLayoutRef = useRef(openLayout)
  const nodesRef = useRef(nodes)
  selectNodeIdRef.current = selectNodeId
  openLayoutRef.current = openLayout
  nodesRef.current = nodes
  const active = board
  const activeFlowId = board?.id ?? null

  const {
    deletedIdsRef,
    deletedEdgeIdsRef,
    recordCreate,
    recordCreatedGraph,
    stageNodes,
    deleteNode,
    deleteNodes,
    connectNodes,
    disconnectEdge,
    moveNodes,
    resizeNode,
    undo,
    redo,
  } = useFlowBoardHistory({
    projectId: projectId,
    flowId: activeFlowId,
    canEdit,
    nodeRows,
    setNodeRows,
    setSelectedIds,
    onGraphChange: onStructureChange,
  })
  const boardIdRef = useRef(activeFlowId)
  const clipboardRef = useRef<FlowNodeClipboard | null>(null)
  const pasteLockRef = useRef(false)
  const zoomRef = useRef(board?.viewport_zoom ?? 1)
  const uploadTempIdRef = useRef(0)
  const uploadsRef = useRef(new Map<number, AbortController>())

  useEffect(() => {
    const switched = boardIdRef.current !== activeFlowId
    boardIdRef.current = activeFlowId
    setNodeRows((prev) => {
      const next = switched ? nodes : mergeFlowNodeRows(prev, nodes, deletedIdsRef.current, deletedEdgeIdsRef.current)
      if (next.length === prev.length && next.every((node, index) => node === prev[index])) return prev
      return next
    })
  }, [activeFlowId, deletedEdgeIdsRef, deletedIdsRef, nodes])

  useEffect(() => {
    clipboardRef.current = null
    setSelectedEdgeId(null)
    setMenu(null)
    setVersionsNodeId(null)
    setMentionedNodeIds([])

    const rows = nodesRef.current
    const pinnedId = selectNodeIdRef.current
    if (pinnedId !== undefined) {
      if (pinnedId != null && rows.some((node) => node.id === pinnedId)) {
        setSelectedIds([pinnedId])
        // Общий кадр и камера на одну ноду спорят за viewport. Выделение ноды остаётся.
        setFocusNodeId(openLayoutRef.current ? null : pinnedId)
      } else {
        setSelectedIds([])
        setFocusNodeId(null)
      }
      return
    }
    const focusId = parseFlowFocusNodeId(pageUrl)
    const alreadyConsumed = consumedFocusUrlRef.current === pageUrl
    if (!alreadyConsumed && focusId != null && rows.some((node) => node.id === focusId)) {
      consumedFocusUrlRef.current = pageUrl
      setSelectedIds([focusId])
      setFocusNodeId(focusId)
      stripFlowFocusNodeParam()
    } else {
      setSelectedIds([])
      setFocusNodeId(null)
    }
    // nodes и pageUrl — с того же рендера, что сменил доску. Поздние обновления списка
    // не должны сбрасывать выделение и заново наводиться на ноду.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFlowId])

  const zoomFlowRef = useRef(active?.id ?? null)
  if (zoomFlowRef.current !== (active?.id ?? null)) {
    zoomFlowRef.current = active?.id ?? null
    zoomRef.current = active?.viewport_zoom ?? 1
  }
  const selectedRow = selectedIds.length === 1 ? (nodeRows.find((node) => node.id === selectedIds[0]) ?? null) : null
  const selected = selectedRow && !flowNodeUploading(selectedRow) ? selectedRow : null
  const reportMentionedNodeIds = useCallback((ids: number[]) => {
    setMentionedNodeIds((current) => (sameIdSet(current, ids) ? current : ids))
  }, [])
  const versionsNode = nodeRows.find((node) => node.id === versionsNodeId) ?? null
  const shotIdsOnBoard = useMemo(() => {
    const ids = new Set<number>()
    for (const node of nodeRows) {
      if (node.kind === "shot" && node.shot_id != null) ids.add(node.shot_id)
    }
    return ids
  }, [nodeRows])
  const busyShotIds = useMemo(() => {
    const ids = new Set<number>()
    for (const job of jobs) {
      if (!SHOT_MEDIA_JOB_KINDS.has(job.job_kind) || !shotMediaJobActive(job)) continue
      for (const id of job.shot_ids ?? []) {
        if (shotIdsOnBoard.has(id)) ids.add(id)
      }
    }
    return ids
  }, [jobs, shotIdsOnBoard])
  const canvasRows = useMemo(
    () =>
      busyShotIds.size === 0
        ? nodeRows
        : nodeRows.map((node) =>
            node.kind === "shot" && node.shot_id != null && busyShotIds.has(node.shot_id)
              ? { ...node, generation_status: "processing" as const }
              : node,
          ),
    [busyShotIds, nodeRows],
  )
  const pending = canvasRows.some(flowNodePending) || canvasRows.some((node) => node.batch_waiting)

  useEffect(() => {
    if (!pending) return
    const timer = window.setInterval(() => {
      reloadNodes()
    }, 4000)
    return () => window.clearInterval(timer)
  }, [pending, reloadNodes])

  useEffect(() => {
    const previous = flowNodeRunStatusRef.current
    const next: Record<number, string> = {}
    let shouldReload = false
    for (const run of Object.values(agentRuns)) {
      if (run.context_type !== "flow_node" && run.context_type !== "flow") continue
      next[run.conversation_id] = run.status
      const prev = previous[run.conversation_id]
      const wasActive = prev === "processing" || prev === "cancel_requested"
      if (wasActive && run.status === "idle") shouldReload = true
    }
    flowNodeRunStatusRef.current = next
    if (!shouldReload) return
    reloadNodes()
  }, [agentRuns, reloadNodes])

  useEffect(() => {
    const previous = shotJobStatusRef.current
    const next: Record<number, string> = {}
    let shouldReload = false
    for (const job of jobs) {
      if (!SHOT_MEDIA_JOB_KINDS.has(job.job_kind)) continue
      const touchesBoard = (job.shot_ids ?? []).some((id) => shotIdsOnBoard.has(id))
      if (!touchesBoard) continue
      next[job.id] = job.status
      const prev = previous[job.id]
      const wasActive = prev === "queued" || prev === "processing"
      const finished = job.status === "completed" || job.status === "failed" || job.status === "cancelled"
      if (wasActive && finished) shouldReload = true
    }
    shotJobStatusRef.current = next
    if (!shouldReload) return
    reloadNodes()
  }, [jobs, reloadNodes, shotIdsOnBoard])

  const mentionItems = useMemo(
    () => buildMentionItems({ characters, locations, props: propsList }),
    [characters, locations, propsList],
  )

  const replaceNode = useCallback((next: FlowNodeRow) => {
    setNodeRows((prev) => prev.map((node) => (node.id === next.id ? next : node)))
  }, [])

  /** Оптимистичная правка ноды с откатом, если сервер отказал. */
  const patchNodeOptimistic = useCallback(
    (previous: FlowNodeRow, patch: { title: string | null } | { chain_input: boolean }) => {
      replaceNode({ ...previous, ...patch })
      void updateFlowNode(projectId, previous.id, patch)
        .then((next) => {
          replaceNode(next)
          onStructureChange?.()
        })
        .catch((error: unknown) => {
          replaceNode(previous)
          toastNodeError(error)
        })
    },
    [onStructureChange, projectId, replaceNode, toastNodeError],
  )

  const renameTitle = useCallback(
    (nodeId: number, title: string | null) => {
      const previous = nodeRows.find((node) => node.id === nodeId)
      if (!previous || flowNodeUploading(previous) || previous.title === title) return
      patchNodeOptimistic(previous, { title })
    },
    [nodeRows, patchNodeOptimistic],
  )

  const setChainInput = useCallback(
    (nodeId: number, chainInput: boolean) => {
      const previous = nodeRows.find((node) => node.id === nodeId)
      if (!previous || Boolean(previous.chain_input) === chainInput) return
      patchNodeOptimistic(previous, { chain_input: chainInput })
    },
    [nodeRows, patchNodeOptimistic],
  )

  const patchNode = useCallback((nodeId: number, patch: Pick<FlowNodeRow, "prompt_text" | "prompt_doc">) => {
    setNodeRows((prev) => prev.map((node) => (node.id === nodeId ? { ...node, ...patch } : node)))
  }, [])

  const attachInputs = useCallback(
    async (targetId: number, batch: FlowAttachRequest): Promise<FlowAttachOutcome> => {
      const board = active
      const target = nodeRows.find((node) => node.id === targetId)
      if (!board || !canEdit || !target) return { attached: 0, failed: 1 }
      const allowed = flowAttachMediaForTarget(target.kind)
      if (allowed.length === 0) return { attached: 0, failed: 1 }

      const partition = partitionFlowFiles(batch.files ?? [])
      const files = partition.accepted.filter((item) => allowed.includes(item.kind))
      const picks = (batch.picks ?? []).filter((pick) => allowed.includes(pick.media))
      const dropped =
        partition.rejectedType ||
        files.length !== partition.accepted.length ||
        picks.length !== (batch.picks?.length ?? 0)
      if (dropped) toast.error(t("referenceInputs.unsupportedFileType", { ns: "common" }))
      if (partition.oversized) {
        toast.error(t("referenceInputs.fileTooLarge", { ns: "common", max: referenceMaxUploadMb(partition.oversized) }))
      }

      const limit = flowAttachLimit(target.kind, target.inputs)
      const fileItems = limit == null ? files : files.slice(0, limit)
      const pickItems = limit == null ? picks : picks.slice(0, limit)
      let cursor = target
      let attached = 0
      let failed = dropped || partition.oversized != null ? 1 : 0
      const created: FlowNodeRow[] = []
      const edgeIds: number[] = []
      const boardId = board.id

      const connectCreated = async (
        source: FlowNodeRow,
        media: FlowAttachMedia,
        bundle: FlowNodeRow[],
      ): Promise<boolean> => {
        const ports = flowAttachPorts(cursor, media, batch.targetPort)
        if (!ports) {
          toast.error(t("errors.nodeFailed"))
          try {
            await deleteFlowNode(projectId, source.id)
          } catch {
            // Нода без ребра всё равно не должна остаться, но исходная ошибка уже показана.
          }
          return false
        }
        try {
          const linked = await createFlowEdge(projectId, board.id, source.id, cursor.id, {
            sourcePort: ports.sourcePort,
            targetPort: ports.targetPort,
          })
          if (boardIdRef.current !== boardId) return false
          let next = linked.node.id === cursor.id ? linked.node : cursor
          setNodeRows((prev) => mergeFlowNodes(prev, [...bundle, ...linked.nodes]))
          if (batch.slotIndex != null) {
            const pinned = await updateFlowNode(projectId, next.id, {
              slot_index: batch.slotIndex,
              source_node_id: source.id,
            })
            if (boardIdRef.current !== boardId) return false
            setNodeRows((prev) => mergeFlowNodes(prev, [pinned]))
            if (pinned.id === next.id) next = pinned
          }
          const edgeId = (next.inputs ?? []).find(
            (input) => input.source_node_id === source.id && (input.source_port ?? "image") === ports.sourcePort,
          )?.id
          if (edgeId != null) edgeIds.push(edgeId)
          created.push(source)
          cursor = next
          return true
        } catch (error: unknown) {
          try {
            await deleteFlowNode(projectId, source.id)
          } catch {
            // Ребро не встало — ноду убираем. Если удаление само упало, тост всё равно про связь.
          }
          toastNodeError(error)
          return false
        }
      }

      for (const item of fileItems) {
        if (boardIdRef.current !== boardId) break
        const ports = flowAttachPorts(cursor, item.kind, batch.targetPort)
        if (!ports) {
          failed += 1
          toast.error(t("errors.nodeFailed"))
          continue
        }
        const place = flowAttachPosition(cursor, 0)
        try {
          const createdNode = await createFlowNode(projectId, board.id, {
            kind: item.kind,
            positionX: place.positionX,
            positionY: place.positionY,
            file: item.file,
          })
          const ok = await connectCreated(createdNode.node, item.kind, createdNode.nodes)
          if (ok) attached += 1
          else failed += 1
        } catch (error: unknown) {
          failed += 1
          toastNodeError(error)
        }
      }

      for (const pick of pickItems) {
        if (boardIdRef.current !== boardId) break
        const place = flowAttachPosition(cursor, 0)
        try {
          const createdNode = await createFlowNode(projectId, board.id, {
            kind: "library",
            positionX: place.positionX,
            positionY: place.positionY,
            source: pick.source,
            mediaExpect: allowed,
          })
          const media = flowAttachMediaFromContentType(createdNode.node.content_type)
          if (!media || !allowed.includes(media)) {
            failed += 1
            toast.error(t("referenceInputs.unsupportedFileType", { ns: "common" }))
            try {
              await deleteFlowNode(projectId, createdNode.node.id)
            } catch {
              // Тип не подошёл — ноду не оставляем на доске.
            }
            continue
          }
          const ok = await connectCreated(createdNode.node, media, createdNode.nodes)
          if (ok) attached += 1
          else failed += 1
        } catch (error: unknown) {
          failed += 1
          toastNodeError(error)
        }
      }

      if (boardIdRef.current === boardId && created.length > 0) {
        recordCreatedGraph(created, { edgeIds })
        onStructureChange?.()
      }
      return { attached, failed }
    },
    [active, canEdit, nodeRows, onStructureChange, projectId, recordCreatedGraph, t, toastNodeError],
  )

  const cancelUpload = useCallback((id: number) => {
    const controller = uploadsRef.current.get(id)
    uploadsRef.current.delete(id)
    controller?.abort()
    setNodeRows((prev) => (prev.some((node) => node.id === id) ? prev.filter((node) => node.id !== id) : prev))
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : prev))
  }, [])

  const confirmDeleteNodes = useCallback(
    async (ids: number[]) => {
      const waiting = nodeRows.filter((node) => ids.includes(node.id) && node.batch_waiting)
      if (waiting.length > 0) {
        const ok = await confirm({
          title: t("bulk.deleteTitle"),
          message: waiting.length === 1 ? t("bulk.deleteNode") : t("bulk.deleteNodes"),
          confirmLabel: t("menu.delete"),
          tone: "danger",
        })
        if (!ok) return
      }
      const uploading = ids.filter((id) => nodeRows.some((node) => node.id === id && flowNodeUploading(node)))
      const rest = ids.filter((id) => !uploading.includes(id))
      for (const id of uploading) cancelUpload(id)
      if (rest.length === 0) return
      if (rest.length === 1) deleteNode(rest[0]!)
      else deleteNodes(rest)
    },
    [cancelUpload, deleteNode, deleteNodes, nodeRows, t],
  )

  const confirmDisconnectEdge = useCallback(
    async (edgeId: number) => {
      if (edgeTouchesWaiting(nodeRows, edgeId)) {
        const ok = await confirm({
          message: t("bulk.deleteEdge"),
          confirmLabel: t("menu.delete"),
          tone: "danger",
        })
        if (!ok) return
      }
      setSelectedEdgeId((current) => (current === edgeId ? null : current))
      disconnectEdge(edgeId)
    },
    [disconnectEdge, nodeRows, t],
  )

  const panel = useFlowNodePanel({
    projectId: projectId,
    projectName,
    activeFlowId: active?.id ?? null,
    node: selected,
    models,
    upscaleModels,
    frameFormat: defaults.frame_format,
    projectStyles,
    mentionItems,
    canEdit,
    nodes: nodeRows,
    onDisconnectEdge: (edgeId) => {
      void confirmDisconnectEdge(edgeId)
    },
    onNodeChange: replaceNode,
    onNodesChange: (nodes) => setNodeRows((prev) => mergeFlowNodes(prev, nodes)),
    onNodePatch: patchNode,
    frameRate: project?.effective_video_output_fps,
    onAttachInputs: attachInputs,
    onFlowBoardChanged: reloadNodes,
    onMentionedFlowNodeIdsChange: reportMentionedNodeIds,
  })

  const addNode = useCallback(
    async (
      kind: FlowNodeKind,
      extra?: {
        file?: File
        source?: ShotFrameSource
        shotId?: number
        sceneId?: number
        entityId?: number
        chainId?: number
        /** Конец превью связи. Позиция create — прогноз, после ответа поджимается под реальный кадр. */
        connectDrop?: { x: number; y: number; direction: "in" | "out" }
      },
    ) => {
      if (!active || !canEdit) return
      const boardId = active.id
      const created = await createFlowNode(projectId, boardId, {
        kind,
        positionX: placeAt.current.x,
        positionY: placeAt.current.y,
        file: extra?.file,
        source: extra?.source,
        shotId: extra?.shotId,
        sceneId: extra?.sceneId,
        newShot: extra?.sceneId != null && extra.shotId == null,
        entityId: extra?.entityId,
        chainId: extra?.chainId,
      })
      // Пока шёл create, панель шота могла переключиться на другую доску.
      if (boardIdRef.current !== boardId) return
      const drop = extra?.connectDrop
      const fitted = drop ? flowConnectOriginForNode(drop, created.node, flowConnectHandleSide(drop.direction)) : null
      const moved =
        fitted != null &&
        (Math.abs(fitted.x - created.node.position_x) >= 0.5 || Math.abs(fitted.y - created.node.position_y) >= 0.5)
      const node = moved && fitted ? { ...created.node, position_x: fitted.x, position_y: fitted.y } : created.node
      const nodes =
        moved && fitted
          ? created.nodes.map((item) =>
              item.id === node.id ? { ...item, position_x: fitted.x, position_y: fitted.y } : item,
            )
          : created.nodes
      setNodeRows((prev) => mergeFlowNodes(prev, nodes))
      stageNodes(nodes)
      setSelectedIds([node.id])
      if (kind === "shot") recordCreatedGraph(nodes, { includeEdges: false })
      else recordCreate(node)
      onStructureChange?.()
      if (moved && fitted) {
        const sentX = created.node.position_x
        const sentY = created.node.position_y
        void updateFlowNode(projectId, node.id, { position_x: fitted.x, position_y: fitted.y }).catch(
          (error: unknown) => {
            if (boardIdRef.current !== boardId) return
            setNodeRows((prev) =>
              prev.map((item) => (item.id === node.id ? { ...item, position_x: sentX, position_y: sentY } : item)),
            )
            toastNodeError(error)
          },
        )
      }
      return { node, nodes }
    },
    [active, canEdit, onStructureChange, projectId, recordCreate, recordCreatedGraph, stageNodes, toastNodeError],
  )

  const placeFiles = useCallback(
    async (files: File[], originX: number, originY: number, zoom: number) => {
      if (!active || !canEdit || files.length === 0) return null
      const { accepted, rejectedType, oversized } = partitionFlowFiles(files)
      if (rejectedType) toast.error(t("referenceInputs.unsupportedFileType", { ns: "common" }))
      if (oversized) {
        toast.error(t("referenceInputs.fileTooLarge", { ns: "common", max: referenceMaxUploadMb(oversized) }))
      }
      if (accepted.length === 0) return null
      const boardId = active.id
      const placeholders = accepted.map((item, index) => {
        const offset = flowFileCascadeOffset(index, zoom)
        uploadTempIdRef.current -= 1
        return flowUploadingNodeRow({
          id: uploadTempIdRef.current,
          kind: item.kind,
          positionX: originX + offset,
          positionY: originY + offset,
          title: flowFileNodeTitle(item.file.name),
        })
      })
      setNodeRows((prev) => mergeFlowNodes(prev, placeholders))

      const createdNodes = await Promise.all(
        placeholders.map(async (placeholder, index) => {
          const item = accepted[index]
          if (!item) return null
          const controller = new AbortController()
          uploadsRef.current.set(placeholder.id, controller)
          try {
            const created = await createFlowNode(projectId, boardId, {
              kind: item.kind,
              positionX: placeholder.position_x,
              positionY: placeholder.position_y,
              file: item.file,
              title: placeholder.title ?? undefined,
              signal: controller.signal,
              onProgress: (percent) => {
                if (boardIdRef.current !== boardId) return
                setNodeRows((prev) =>
                  prev.map((node) =>
                    node.id === placeholder.id && node.upload_percent !== percent
                      ? { ...node, upload_percent: percent }
                      : node,
                  ),
                )
              },
            })
            // Удаление во время заливки уже сняло заготовку. Серверную ноду тогда тоже убираем.
            if (!uploadsRef.current.delete(placeholder.id)) {
              void deleteFlowNode(projectId, created.node.id).catch(() => {})
              return null
            }
            // Файл остаётся на своей доске; в открытую с тех пор чужую доску его не вливаем.
            if (boardIdRef.current !== boardId) return null
            setNodeRows((prev) =>
              mergeFlowNodes(
                prev.filter((node) => node.id !== placeholder.id),
                created.nodes,
              ),
            )
            setSelectedIds((prev) => prev.map((id) => (id === placeholder.id ? created.node.id : id)))
            recordCreate(created.node)
            return created.node
          } catch (error: unknown) {
            uploadsRef.current.delete(placeholder.id)
            if (boardIdRef.current !== boardId) return null
            setNodeRows((prev) => prev.filter((node) => node.id !== placeholder.id))
            setSelectedIds((prev) =>
              prev.includes(placeholder.id) ? prev.filter((id) => id !== placeholder.id) : prev,
            )
            if (!isAbortError(error)) toastNodeError(error)
            return null
          }
        }),
      )

      if (boardIdRef.current !== boardId) return null
      let placed: FlowNodeRow | null = null
      for (const node of createdNodes) if (node) placed = node
      if (placed) {
        setSelectedIds([placed.id])
        onStructureChange?.()
      }
      return placed
    },
    [active, canEdit, onStructureChange, projectId, recordCreate, t, toastNodeError],
  )

  const closeMenu = useCallback(() => setMenu(null), [])
  const openChain = useCallback(
    (chain: FlowChainRow, message: string) => {
      closeMenu()
      onChainsChange([...chains.filter((item) => item.id !== chain.id), chain])
      toast.success(message)
      router.visit(flowChainHref(projectId, chain.id, returnFlow, chainVia))
    },
    [chainVia, chains, closeMenu, onChainsChange, projectId, returnFlow],
  )
  const duplicateChain = useCallback(
    (chainId: number) => {
      void duplicateFlowChain(projectId, chainId)
        .then((chain) => openChain(chain, t("chain.duplicated")))
        .catch((error: unknown) => {
          toastNodeError(error)
        })
    },
    [openChain, projectId, t, toastNodeError],
  )
  const removeChain = useCallback(
    (chainId: number) => {
      const title = chains.find((item) => item.id === chainId)?.title ?? ""
      closeMenu()
      void (async () => {
        const ok = await confirm({
          title: t("chain.deleteTitle"),
          message: t("chain.deleteConfirm", { title }),
          confirmLabel: t("chain.delete"),
          tone: "danger",
        })
        if (!ok) return
        try {
          await deleteFlowChain(projectId, chainId)
          if (editingChainId === chainId) {
            router.visit(flowChainEditorBackHref(projectId, chainId, returnFlow, chainVia))
            return
          }
          onChainsChange(chains.filter((item) => item.id !== chainId))
          reloadNodes()
        } catch (error: unknown) {
          toastNodeError(error)
        }
      })()
    },
    [
      chainVia,
      chains,
      closeMenu,
      editingChainId,
      onChainsChange,
      projectId,
      reloadNodes,
      returnFlow,
      t,
      toastNodeError,
    ],
  )
  const openNewChain = useCallback(
    (request: Promise<FlowChainRow>) => {
      void request
        .then((chain) => openChain(chain, t("chain.created")))
        .catch((error: unknown) => {
          toastNodeError(error)
        })
    },
    [openChain, t, toastNodeError],
  )
  const connectCreated = (from: FlowMenuConnectFrom, created: FlowNodeRow) => {
    const ports = flowStoredPorts(from, created)
    if (!ports) return
    if (from.direction === "in") {
      connectNodes(created.id, from.nodeId, ports)
      return
    }
    connectNodes(from.nodeId, created.id, ports)
  }
  const addMenuNode = (
    kind: FlowNodeKind,
    extra?: { shotId?: number; sceneId?: number; entityId?: number; chainId?: number },
  ) => {
    const from = menu?.connectFrom ?? null
    const drop = from && menu ? { x: menu.worldX, y: menu.worldY, direction: from.direction } : null
    if (drop) {
      placeAt.current = flowConnectNodeOrigin(
        drop,
        flowNodeCardSize(flowFreshNodeRatio(kind, defaults.frame_format)),
        flowConnectHandleSide(drop.direction),
      )
    }
    closeMenu()
    void addNode(kind, { ...extra, connectDrop: drop ?? undefined })
      .then((created) => {
        if (!created || !from) return
        connectCreated(from, created.node)
      })
      .catch((error: unknown) => {
        toastNodeError(error)
      })
  }
  const selectIds = useCallback((ids: number[]) => {
    setMenu(null)
    setSelectedIds((current) => (sameIdSet(current, ids) ? current : ids))
    if (ids.length > 0) setSelectedEdgeId(null)
  }, [])
  const selectEdge = useCallback((id: number | null) => {
    setSelectedEdgeId(id)
    if (id != null) setSelectedIds([])
  }, [])
  const openVersions = useCallback((nodeId: number) => setVersionsNodeId(nodeId), [])
  const rememberZoom = useCallback((zoom: number) => {
    zoomRef.current = zoom
  }, [])
  const pasteCopiedNodes = useCallback(async () => {
    const clip = clipboardRef.current
    const board = active
    if (!clip || !board || !canEdit || clip.flowId !== board.id || clip.nodes.length === 0) return
    if (pasteLockRef.current) return
    pasteLockRef.current = true
    clip.pasteCount += 1
    const placements = flowPastePositions(clip.nodes, clip.pasteCount, zoomRef.current)
    try {
      const created = await duplicateFlowNodes(projectId, board.id, placements)
      if (boardIdRef.current !== board.id) return
      setNodeRows((prev) => {
        const ids = new Set(created.map((node) => node.id))
        return prev.filter((node) => !ids.has(node.id)).concat(created)
      })
      setMenu(null)
      setSelectedEdgeId(null)
      setSelectedIds(created.map((node) => node.id))
      recordCreatedGraph(created)
      onStructureChange?.()
    } catch (error: unknown) {
      clip.pasteCount -= 1
      toastNodeError(error)
    } finally {
      pasteLockRef.current = false
    }
  }, [active, canEdit, onStructureChange, projectId, recordCreatedGraph, toastNodeError])

  useEffect(() => {
    // Панель шота держит сессию смонтированной на любой странице: без открытой доски
    // хоткеи не вешаем, иначе Cmd+A, Cmd+Z и Backspace гасились бы по всему приложению.
    if (activeFlowId == null) return
    const onKey = (event: KeyboardEvent) => {
      if (isFlowBoardTypingTarget(event.target)) return
      if (shortcutBlockedByDialog(insideDialog)) return
      const meta = event.metaKey || event.ctrlKey
      if (meta && event.code === "KeyA" && !event.shiftKey && !event.altKey) {
        event.preventDefault()
        setMenu(null)
        setSelectedEdgeId(null)
        setSelectedIds(nodeRows.map((node) => node.id))
        return
      }
      if (!canEdit) return
      if (meta && event.code === "KeyZ" && !event.altKey) {
        event.preventDefault()
        if (event.shiftKey) redo()
        else undo()
        return
      }
      if (meta || event.altKey) return
      if (event.key !== "Delete" && event.key !== "Backspace") return
      event.preventDefault()
      setMenu(null)
      if (selectedEdgeId != null && selectedIds.length === 0) {
        void confirmDisconnectEdge(selectedEdgeId)
        return
      }
      if (selectedIds.length === 0) return
      void confirmDeleteNodes(selectedIds)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [
    activeFlowId,
    canEdit,
    confirmDeleteNodes,
    confirmDisconnectEdge,
    insideDialog,
    nodeRows,
    redo,
    selectedEdgeId,
    selectedIds,
    undo,
  ])

  useEffect(() => {
    if (activeFlowId == null) return
    const blocked = (target: EventTarget | null) =>
      isFlowBoardTypingTarget(target) || shortcutBlockedByDialog(insideDialog)

    const onCopy = (event: ClipboardEvent) => {
      if (blocked(event.target) || !canEdit || selectedIds.length === 0) return
      // Выделенный текст (промпт в чате, подпись ноды) уходит в буфер как есть.
      // Иначе Cmd+C подменяется меткой нод.
      if ((window.getSelection()?.toString() ?? "") !== "") return
      const byId = new Map(nodeRows.map((node) => [node.id, node]))
      const picked = selectedIds.flatMap((id) => {
        const node = byId.get(id)
        return node && !flowNodeUploading(node) ? [node] : []
      })
      if (picked.length === 0 || activeFlowId == null) return
      const nonce =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`
      event.preventDefault()
      event.clipboardData?.setData("text/plain", `${FLOW_NODE_CLIPBOARD_MARKER}:${nonce}`)
      clipboardRef.current = {
        flowId: activeFlowId,
        nonce,
        pasteCount: 0,
        nodes: picked.map((node) => ({ id: node.id, x: node.position_x, y: node.position_y })),
      }
    }

    const onPaste = (event: ClipboardEvent) => {
      if (blocked(event.target) || !canEdit) return
      const clip = clipboardRef.current
      if (!clip || clip.flowId !== activeFlowId) return
      const text = event.clipboardData?.getData("text/plain") ?? ""
      if (text !== `${FLOW_NODE_CLIPBOARD_MARKER}:${clip.nonce}`) return
      if (clipboardHasFiles(event.clipboardData)) return
      event.preventDefault()
      void pasteCopiedNodes()
    }

    window.addEventListener("copy", onCopy)
    window.addEventListener("paste", onPaste, true)
    return () => {
      window.removeEventListener("copy", onCopy)
      window.removeEventListener("paste", onPaste, true)
    }
  }, [activeFlowId, canEdit, insideDialog, nodeRows, pasteCopiedNodes, selectedIds])

  const canvas = active ? (
    <FlowNodeTitleProvider
      canEdit={canEdit}
      renameTitle={renameTitle}
      chainInputs={chainInputs}
      setChainInput={setChainInput}
    >
      <FlowVersionsProvider onOpen={openVersions}>
        <FlowCanvas
          key={active.id}
          projectId={projectId}
          rows={canvasRows}
          selectedIds={selectedIds}
          mentionedNodeIds={mentionedNodeIds}
          focusNodeId={focusNodeId}
          openLayout={openLayout}
          chainNotice={chainNotice}
          canEdit={canEdit}
          viewport={{ x: active.viewport_x, y: active.viewport_y, zoom: active.viewport_zoom }}
          onSelectIds={selectIds}
          onSelectEdge={selectEdge}
          onConnectNodes={connectNodes}
          onMoveNodes={(updates) => {
            const live = updates.filter((update) => !uploadsRef.current.has(update.id))
            if (live.length > 0) moveNodes(live)
          }}
          onResizeNode={(id, width, position) => {
            if (uploadsRef.current.has(id)) return
            resizeNode(id, width, position)
          }}
          onZoom={rememberZoom}
          onViewportChange={(viewport) => {
            zoomRef.current = viewport.zoom
            void updateFlowViewport(projectId, active.id, viewport)
          }}
          onPaneMenu={(x, y, worldX, worldY) => {
            placeAt.current = { x: worldX, y: worldY }
            setMenu({ x, y, worldX, worldY, nodeId: null })
          }}
          onConnectDropMenu={(x, y, worldX, worldY, connectFrom) => {
            placeAt.current = { x: worldX, y: worldY }
            setMenu({ x, y, worldX, worldY, nodeId: null, connectFrom })
          }}
          onNodeMenu={(x, y, nodeId) => {
            setMenu({ x, y, worldX: 0, worldY: 0, nodeId })
          }}
          onPlaceFiles={(files, worldX, worldY, zoom) => {
            void placeFiles(files, worldX, worldY, zoom)
          }}
          connectPreview={
            menu?.connectFrom
              ? {
                  sourceId: menu.connectFrom.nodeId,
                  sourcePort: menu.connectFrom.port,
                  side: menu.connectFrom.direction === "in" ? "target" : "source",
                  x: menu.worldX,
                  y: menu.worldY,
                }
              : null
          }
        />
      </FlowVersionsProvider>
    </FlowNodeTitleProvider>
  ) : null

  const menuChainId = (() => {
    if (menu?.nodeId == null) return null
    const node = nodeRows.find((item) => item.id === menu.nodeId)
    if (node?.kind !== "chain" || node.chain_id == null) return null
    return node.chain_id
  })()

  const overlays = (
    <>
      {menu ? (
        <FlowContextMenu
          menu={menu}
          projectId={projectId}
          canEdit={canEdit}
          characters={characters}
          locations={locations}
          props={propsList}
          onClose={closeMenu}
          onUpload={() => {
            connectAfterFileRef.current = menu.connectFrom ?? null
            closeMenu()
            fileInputRef.current?.click()
          }}
          onLibrary={() => {
            connectAfterFileRef.current = menu.connectFrom ?? null
            closeMenu()
            setLibraryOpen(true)
          }}
          onImage={() => {
            addMenuNode("image")
          }}
          onVideo={() => {
            addMenuNode("video")
          }}
          onVideoTransition={() => {
            addMenuNode("video_transition")
          }}
          onAudio={() => {
            connectAfterFileRef.current = menu.connectFrom ?? null
            closeMenu()
            audioInputRef.current?.click()
          }}
          onUpscale={() => {
            addMenuNode("video_upscale")
          }}
          onDepth={() => {
            addMenuNode("video_depth")
          }}
          onShot={(shotId) => {
            addMenuNode("shot", { shotId })
          }}
          onNewShot={(sceneId) => {
            addMenuNode("shot", { sceneId })
          }}
          onCharacter={(entityId) => {
            addMenuNode("character", { entityId })
          }}
          onLocation={(entityId) => {
            addMenuNode("location", { entityId })
          }}
          onProp={(entityId) => {
            addMenuNode("prop", { entityId })
          }}
          chains={allowChains ? placeableFlowChains(chains, editingChainId) : []}
          onCreateBlankChain={
            allowChains
              ? () => {
                  closeMenu()
                  openNewChain(createBlankFlowChain(projectId))
                }
              : undefined
          }
          onChain={(chainId) => {
            addMenuNode("chain", { chainId })
          }}
          onEditChain={(chainId) => {
            closeMenu()
            router.visit(flowChainHref(projectId, chainId, returnFlow, chainVia))
          }}
          onDuplicateChain={duplicateChain}
          onDeleteChain={removeChain}
          editChainId={menuChainId}
          canCreateChain={
            allowChains &&
            menu.nodeId != null &&
            selectedIds.includes(menu.nodeId) &&
            selectedIds.filter((id) => {
              const node = nodeRows.find((item) => item.id === id)
              return node != null && !flowNodeUploading(node)
            }).length >= 2
          }
          onCreateChain={() => {
            const ids = selectedIds.filter((id) => {
              const node = nodeRows.find((item) => item.id === id)
              return node != null && !flowNodeUploading(node)
            })
            const board = active
            closeMenu()
            if (!board || ids.length < 2) return
            openNewChain(createFlowChain(projectId, board.id, ids))
          }}
          canRegenerate={
            menu.nodeId != null &&
            selectedIds.includes(menu.nodeId) &&
            selectedIds.length >= 2 &&
            nodeRows.some((node) => selectedIds.includes(node.id) && flowNodeCanBulkRegenerate(node))
          }
          onRegenerate={() => {
            const ids = selectedIds.filter((id) => {
              const node = nodeRows.find((item) => item.id === id)
              return node != null && flowNodeCanBulkRegenerate(node)
            })
            closeMenu()
            if (ids.length === 0) return
            setBulkIds(ids)
            setBulkOpen(true)
          }}
          onDelete={() => {
            const nodeId = menu.nodeId
            closeMenu()
            if (nodeId == null) return
            const ids = selectedIds.includes(nodeId) && selectedIds.length > 1 ? selectedIds : [nodeId]
            void confirmDeleteNodes(ids)
          }}
        />
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        accept={REFERENCE_IMAGE_VIDEO_FILE_ACCEPT}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ""
          const from = connectAfterFileRef.current
          connectAfterFileRef.current = null
          if (!file) return
          const drop = placeAt.current
          const origin = from
            ? flowConnectNodeOrigin(
                drop,
                flowNodeCardSize(flowFreshNodeRatio("upload", defaults.frame_format)),
                flowConnectHandleSide(from.direction),
              )
            : drop
          void placeFiles([file], origin.x, origin.y, 1).then((created) => {
            if (!created || !from) return
            connectCreated(from, created)
          })
        }}
      />
      <input
        ref={audioInputRef}
        type="file"
        accept={REFERENCE_AUDIO_FILE_ACCEPT}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ""
          const from = connectAfterFileRef.current
          connectAfterFileRef.current = null
          if (!file) return
          const drop = placeAt.current
          const origin = from
            ? flowConnectNodeOrigin(
                drop,
                flowNodeCardSize(flowFreshNodeRatio("audio", defaults.frame_format)),
                flowConnectHandleSide(from.direction),
              )
            : drop
          void placeFiles([file], origin.x, origin.y, 1).then((created) => {
            if (!created || !from) return
            connectCreated(from, created)
          })
        }}
      />
      {versionsNode ? (
        <FlowNodeVersionsGallery
          open
          onOpenChange={(next) => {
            if (!next) setVersionsNodeId(null)
          }}
          projectId={projectId}
          node={versionsNode}
          onChanged={reloadNodes}
        />
      ) : null}
      {active ? (
        <FlowBulkRegenerateDialog
          open={bulkOpen}
          projectId={projectId}
          flowId={active.id}
          nodeIds={bulkIds}
          onOpenChange={setBulkOpen}
          onStarted={(nodes) => setNodeRows((prev) => mergeFlowNodes(prev, nodes))}
        />
      ) : null}
      <LibraryPickerDialog
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        projectId={projectId}
        frameFormat={defaults.frame_format}
        subtitle={t("menu.library")}
        targets={[{ id: "flow", label: t("menu.library"), accepts: ["image", "video"] }]}
        defaultTargetId="flow"
        busyTargetId={libraryBusy ? "flow" : null}
        onPick={(_targetId, source) => {
          if (libraryBusy) return
          const from = connectAfterFileRef.current
          connectAfterFileRef.current = null
          const drop = from ? { x: placeAt.current.x, y: placeAt.current.y, direction: from.direction } : null
          if (drop) {
            placeAt.current = flowConnectNodeOrigin(
              drop,
              flowNodeCardSize(flowFreshNodeRatio("library", defaults.frame_format)),
              flowConnectHandleSide(drop.direction),
            )
          }
          setLibraryBusy(true)
          void addNode("library", { source, connectDrop: drop ?? undefined })
            .then((created) => {
              if (created && from) connectCreated(from, created.node)
              setLibraryOpen(false)
            })
            .catch((error: unknown) => {
              toastNodeError(error)
            })
            .finally(() => setLibraryBusy(false))
        }}
      />
    </>
  )

  return {
    canvas,
    sideColumn: <FlowNodeSideColumn panel={panel} />,
    dock: <FlowNodeDock panel={panel} />,
    overlays,
    title: active?.title ?? null,
    nodes: nodeRows,
    renameTitle,
  }
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError"
}

function edgeTouchesWaiting(nodes: FlowNodeRow[], edgeId: number): boolean {
  for (const node of nodes) {
    const input = node.inputs?.find((item) => item.id === edgeId)
    if (!input) continue
    if (node.batch_waiting) return true
    return nodes.some((item) => item.id === input.source_node_id && item.batch_waiting)
  }
  return false
}
